import axios, { AxiosError } from 'axios';

import { User, UserInfo, UserSignIn } from 'models';
import { AuthConfig } from 'models';

import { UserAuthProvider } from 'sources/user-auth-provider';
import { OktaAuthProvider } from 'sources/auth-providers/okta';
import { FailureResponse } from '../client/failure-response';
import { BFFAxiosResponse } from '../models/common';

// Using character flags because they make understanding any logging a lot easier
export enum AuthState {
  UNKNOWN = 'U',
  TRUE = 'T',
  FALSE = 'F',
}

// make this abstract with one for okta, extendable to something else in the future.
export class UserAuthSource {
  private authProvider: UserAuthProvider;
  private readonly authConfig: AuthConfig;
  private history;

  public constructor(authConfig: AuthConfig) {
    this.authProvider = null;
    this.authConfig = authConfig;
  }

  public fetchUserInfo(): Promise<UserInfo> {
    if (this.authProvider) {
      return this.authProvider.getUser();
    }
  }

  public hydrate() {
    const hydratedAuthConfig = {
      ...this.authConfig,
      redirectUri: `${window.location.origin}/auth/callback`,
      pkce: true,
      history: null,
      devMode: false,
    };

    this.authProvider = new OktaAuthProvider(hydratedAuthConfig);
  }

  public hydrateHistory(history) {
    if (this.authProvider && !this.history) {
      this.history = history;
    }
  }

  public async getUserDetails(currentUser: UserInfo) {
    if (currentUser && currentUser.email) {
      const token: string = await this.getAccessToken();

      return axios
        .get(`/api/v2/users/email/${currentUser.email}`, {
          params: { token },
        })
        .then((response) => Promise.resolve(response.data))
        .catch((error) => Promise.reject(error));
    } else {
      return Promise.reject(new FailureResponse(401, {}));
    }
  }

  public async getProgramRelationships(currentUser: UserInfo) {
    if (currentUser && currentUser.email) {
      const token: string = await this.getAccessToken();

      return axios
        .get(`/api/v1/users/${currentUser.email}/programs`, {
          params: { token },
        })
        .then((response) => Promise.resolve(response.data))
        .catch((error) => Promise.reject(error));
    } else {
      return Promise.reject(new FailureResponse(401, {}));
    }
  }

  public async isAuthenticated(): Promise<AuthState> {
    if (this.authProvider) {
      const authenticated = await this.authProvider.isAuthenticated();

      return authenticated ? AuthState.TRUE : AuthState.FALSE;
    } else {
      return AuthState.UNKNOWN;
    }
  }

  public async authRedirect(params) {
    return (
      (this.authProvider && this.authProvider.redirect(params)) ||
      Promise.reject(new Error('Auth provider not initialized.'))
    );
  }

  public isInLoginRedirect() {
    return this.authProvider && this.authProvider.isInLoginRedirect();
  }

  // This call is for the Auth Provider (at the moment, Okta) to turn the login callback
  // into a valid logged in session.
  public async createAuthenticatedSession() {
    return await ((this.authProvider && this.authProvider.storeAuthTokensFromCallback()) ||
      Promise.reject(new Error('Auth provider not initialized.')));
  }

  public initiateLogin(redirection: boolean, fromUri: string = null) {
    const referrerPath = fromUri ? { pathname: fromUri } : this.history.location;

    localStorage.setItem('secureRouterReferrerPath', JSON.stringify(referrerPath));

    const history = this.history;
    // Need the locale in this URL somehow
    const signinPath = '/en-us/signin';

    return redirection ? history.replace(signinPath) : history.push(signinPath);
  }

  public async closeSession() {
    this.authProvider.closeSession();
  }

  public async signIn({ username, password }: UserSignIn) {
    const params = {
      username,
      password,
    };

    return this.authProvider.signIn(params);
  }

  public async signInWithToken(token: string) {
    return this.authProvider.signInWithToken(token);
  }

  public async revokeAndLogout(path: string) {
    const accessToken = await this.authProvider.getAccessToken();
    const idToken = await this.authProvider.getIdToken();

    const revokableTokens = [
      { token: idToken, type: 'id_token' },
      { token: accessToken, type: 'access_token' },
    ];

    const revokePromises = revokableTokens.map((tokenObject) => {
      return fetch('/api/revokeToken', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessToken,
          token: tokenObject.token,
          tokenType: tokenObject.type,
        }),
      })
        .then((revokeResponse) => {
          return revokeResponse;
        })
        .catch((error) => {
          return error;
        });
    });

    await Promise.all(revokePromises);

    await this.authProvider.logout(path);
    this.history.push(path);

    return;
  }

  public async getIdToken() {
    const idToken = await this.authProvider.getIdToken();

    return idToken;
  }

  public async getAccessToken() {
    const accessToken = await this.authProvider.getAccessToken();

    return accessToken;
  }

  public async renewAccessToken() {
    const renewedAccessToken = await this.authProvider.renewToken();

    return renewedAccessToken;
  }

  public async updateUserEmail(oldEmail: string, user: User, locale: string): Promise<BFFAxiosResponse> {
    const token: string = await this.getAccessToken();
    try {
      const result = await axios.put<BFFAxiosResponse>(`/api/v2/users/${oldEmail}/email`, user, {
        params: { locale, token },
      });

      if (result.data.success === true) {
        try {
          await this.renewAccessToken();
        } catch (oktaError) {
          // Do nothing
        }
      }

      return result.data;
    } catch (error) {
      if (error.response) {
        const { data: errorBody, status } = error.response;

        return {
          success: false,
          errorCode: status.toString(),
          body: errorBody.errorMessage,
        };
      }

      return {
        success: false,
        errorCode: '500',
        body: { message: (error as Error).message },
      };
    }
  }

  public async updateUserOnboardStatus(email: string): Promise<BFFAxiosResponse> {
    try {
      const result = await axios.put<BFFAxiosResponse>(`/api/v1/users/${email}/hasOnboarded`);

      return result.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const { data: errorBody, status } = (error as AxiosError).response;

        return {
          success: false,
          errorCode: status.toString(),
          body: errorBody.errorMessage,
        };
      }

      return {
        success: false,
        errorCode: '500',
        body: { message: (error as Error).message },
      };
    }
  }
}
